import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'

import { ApolloProvider } from '@apollo/client'
import { useEditor } from '@toasttab/sites-components'

import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import { DiningBehavior, LogoSizeOption } from 'src/apollo/sites'
import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'

import LocationMap from 'shared/components/common/location_map/LocationMap'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import {
  RestaurantSiteContent,
  useRestaurant
} from 'shared/components/common/restaurant_context/RestaurantContext'
import NotFound404 from 'shared/components/errors/NotFound404'
import useOOLocations from 'shared/js/hooks/useOOLocations'
import { ScreenWidth, useIsMobile } from 'shared/js/utils/WindowContext'

import PageMeta from 'public/components/default_template/meta/PageMeta'
import Nav from 'public/components/default_template/nav/Nav'
import { CartContextProvider } from 'public/components/online_ordering/CartContext'
import { DeliveryContextProvider } from 'public/components/online_ordering/DeliveryContext'
import { FulfillmentContextProvider } from 'public/components/online_ordering/FulfillmentContext'
import ToastProduct from 'public/components/online_ordering/ToastProduct'
import {
  ListViewIcon,
  MapViewIcon
} from 'public/components/pages/location_selection_page/LocationSelectionIcons'

import LocationPickerWithMap, {
  BasicLocationPicker,
  MAP_SOURCE_ID,
  View
} from './LocationSelection'
import LocationSelectionPageSchema from './LocationSelectionPageSchema'

import { t } from 'src/translations'
import { A11yProvider } from '../../online_ordering/useA11y'

const getDiningOptionBehavior = (
  behavior: DiningBehavior | undefined | null,
  defaultOpt: DiningOptionBehavior
) => {
  switch (behavior) {
    case DiningBehavior.Delivery:
      return DiningOptionBehavior.Delivery
    case DiningBehavior.Takeout:
      return DiningOptionBehavior.TakeOut
    default:
      return defaultOpt
  }
}

type Props = {
  queryParams: URLSearchParams
}

export const CoreLocationSelectionPage = ({ queryParams }: Props) => {
  const { restaurant: siteRestaurant, toastProduct } = useRestaurant()
  const tracker = useTracker()
  const showFullPicker =
    toastProduct === ToastProduct.Sites || toastProduct === ToastProduct.OOPro
  const [view, setView] = useState<View>(View.List)
  const isMobile = useIsMobile(ScreenWidth.SMALL)
  const { meta, content, config, theme } = siteRestaurant

  // Only display Rx locations with OO enabled. An Rx with the Standalone Websites feature, will not be displayed.
  const ooLocations = useOOLocations()

  const paramDiningOption = queryParams?.get(
    'diningOption'
  ) as DiningBehavior | null
  const [selectedDiningOptionBehavior, setSelectedDiningOptionBehavior] =
    useState(
      getDiningOptionBehavior(
        paramDiningOption || config.ooConfig?.defaultDiningBehavior,
        DiningOptionBehavior.TakeOut
      )
    )
  const [customerLocation, setCustomerLocation] =
    useState<google.maps.places.PlaceResult | null>(null)

  const showAllLocationsMode = useMemo(() => {
    return queryParams.get('showAllLocations') === 'true'
  }, [queryParams])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      tracker.track(TrackingEvents.LOCATION_SELECTION_PAGE_LOCATION_PICKER, {
        managementGuid: siteRestaurant.externalId,
        picker: showFullPicker
          ? t('location-selection-page.location-picker-map')
          : t('location-selection-page.basic-location-picker'),
        viewMode: showAllLocationsMode ? 'all-locations' : 'normal'
      })
    }
  }, [siteRestaurant.externalId, tracker, showFullPicker, showAllLocationsMode])

  if (!content) {
    return <NotFound404 meta={meta} siteTheme={theme} />
  }

  const { primaryCta, nonPrimaryCtas } = content as RestaurantSiteContent
  const title = t('location-selection-page.find-location', {
    restaurantName:
      siteRestaurant.config.ooConfig?.seoMeta?.title || siteRestaurant.name
  })
  const description =
    siteRestaurant.config.ooConfig?.seoMeta?.description ||
    t('location-selection-page.select-location', {
      restaurantName: siteRestaurant.name
    }) ||
    ''

  return (
    <>
      <PageMeta title={title} description={description} />
      <div className='defaultTemplate'>
        <Nav
          withShadow
          navType='stickyNav'
          logoSrc={meta.icon}
          logoObject={meta.iconObject}
          logoSize={LogoSizeOption.Standard}
          primaryCta={primaryCta}
          nonPrimaryCtas={nonPrimaryCtas}
          shouldShowPreviewBanner={false}
          showAuthTooltip
        />
        {showFullPicker ? (
          <main className='locationSelectionPageContent' id='main'>
            <div className='leftColumn'>
              <A11yProvider>
                <LocationPickerWithMap
                  locations={ooLocations}
                  customerLocation={customerLocation}
                  setCustomerLocation={setCustomerLocation}
                  diningOptionBehavior={selectedDiningOptionBehavior}
                  updateDiningBehavior={setSelectedDiningOptionBehavior}
                  view={view}
                  allowMap={Boolean(isMobile)}
                  showAllLocationsMode={showAllLocationsMode}
                />
              </A11yProvider>
              {isMobile && (
                <button
                  className='viewButton'
                  onClick={() =>
                    setView(view === View.Map ? View.List : View.Map)
                  }
                >
                  {view === View.Map ? (
                    <ListViewIcon color={meta.primaryColor} />
                  ) : (
                    <MapViewIcon color={meta.primaryColor} />
                  )}
                  <span>
                    {view === View.Map
                      ? t('location-selection-page.show-list')
                      : t('location-selection-page.show-map')}
                  </span>
                </button>
              )}
            </div>
            {!isMobile && (
              <div className='rightColumn'>
                <LocationMap
                  locationGuids={ooLocations?.map((loc) => loc.externalId)}
                  hideDefaultLocation
                  sourceId={MAP_SOURCE_ID}
                  markerStyle='default'
                  selectedDiningOption={selectedDiningOptionBehavior}
                />
              </div>
            )}
          </main>
        ) : (
          <main className='basicLocationSelectionPageContent' id='main'>
            <A11yProvider>
              <BasicLocationPicker
                locations={ooLocations}
                customerLocation={customerLocation}
                setCustomerLocation={setCustomerLocation}
                diningOptionBehavior={selectedDiningOptionBehavior}
                updateDiningBehavior={setSelectedDiningOptionBehavior}
                view={View.List}
                allowMap={false}
                showAllLocationsMode={showAllLocationsMode}
              />
            </A11yProvider>
          </main>
        )}
      </div>
      <LocationSelectionPageSchema />
    </>
  )
}

const LocationSelectionPageWithRouterDependencies = () => {
  const location = useLocation()
  return (
    <CoreLocationSelectionPage
      queryParams={new URLSearchParams(location.search)}
    />
  )
}

const WrappedLocationSelectionPage = () => {
  const { isEditor } = useEditor()
  const editorQueryParams = new URLSearchParams(
    `diningOption=${DiningOptionBehavior.TakeOut}`
  )
  return isEditor ? (
    <CoreLocationSelectionPage queryParams={editorQueryParams} />
  ) : (
    <LocationSelectionPageWithRouterDependencies />
  )
}

const LocationSelectionPage = React.memo(WrappedLocationSelectionPage)

const ContextualLocationSelectionPage = () => {
  const ooClient = useOOClient()

  return (
    <ApolloProvider client={ooClient}>
      <CartContextProvider>
        <DeliveryContextProvider>
          <FulfillmentContextProvider>
            <LocationSelectionPage />
          </FulfillmentContextProvider>
        </DeliveryContextProvider>
      </CartContextProvider>
    </ApolloProvider>
  )
}

export default ContextualLocationSelectionPage
